import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { globalNetwork, heroImages } from '../data/mockData';
import { Globe, CheckCircle2, ArrowRight } from 'lucide-react';

export const GlobalNetwork = () => {
  const benefits = [
    {
      title: 'Manufacturing Capacity Across Time Zones',
      description: '24/7 production capability with coordinated scheduling'
    },
    {
      title: 'Cost Optimization',
      description: 'Strategic sourcing to leverage regional cost advantages'
    },
    {
      title: 'Supply Chain Redundancy',
      description: 'Risk mitigation through geographic diversification'
    },
    {
      title: 'Consistent Quality Standards',
      description: 'Unified quality management across all locations'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 bg-accent/10 rounded-full flex items-center justify-center">
              <Globe className="h-8 w-8 text-accent" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-foreground mb-6">Global Manufacturing Network</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Strategic locations across Canada, Malaysia, China, Mexico, and Vietnam providing capacity, flexibility, and unified management
          </p>
        </div>
      </section>

      {/* Network Locations */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 xl:grid-cols-5 gap-8">
            {globalNetwork.map((location, index) => (
              <Card key={index} className="hover:shadow-xl transition-smooth">
                <CardContent className="p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="h-12 w-12 bg-accent/10 rounded-full flex items-center justify-center">
                      <Globe className="h-6 w-6 text-accent" />
                    </div>
                    <h2 className="text-3xl font-bold">{location.country}</h2>
                  </div>
                  <p className="text-muted-foreground mb-6 leading-relaxed">{location.description}</p>
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Key Capabilities</h3>
                    {location.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start space-x-2">
                        <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-6 border-t">
                    <Link to={`/global-network/${location.country.toLowerCase()}`}>
                      <Button variant="outline" className="w-full group">
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Benefits of Our Global Network</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Combining international manufacturing strength with Canadian project management
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="hover:shadow-lg transition-smooth">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How We Manage */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Unified Management Approach</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Canadian project management ensures consistent communication and quality oversight across all manufacturing locations
              </p>

              <div className="space-y-4">
                <Card>
                  <CardContent className="p-5">
                    <h3 className="font-bold mb-2">Single Point of Contact</h3>
                    <p className="text-sm text-muted-foreground">
                      Your Canadian project manager handles all coordination, eliminating the complexity of managing multiple suppliers across different time zones
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-5">
                    <h3 className="font-bold mb-2">Quality Oversight</h3>
                    <p className="text-sm text-muted-foreground">
                      Standardized quality processes and inspection protocols across all facilities ensure consistent results regardless of manufacturing location
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-5">
                    <h3 className="font-bold mb-2">Logistics Coordination</h3>
                    <p className="text-sm text-muted-foreground">
                      We handle customs, shipping, and delivery coordination. DDP pricing available for hassle-free international manufacturing
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-5">
                    <h3 className="font-bold mb-2">Risk Management</h3>
                    <p className="text-sm text-muted-foreground">
                      Geographic diversification provides supply chain resilience and capacity flexibility to meet your delivery requirements
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="space-y-6">
              <img 
                src={heroImages.manufacturing} 
                alt="Global Manufacturing" 
                className="rounded-lg shadow-xl"
              />
              <img 
                src={heroImages.factory} 
                alt="Factory Floor" 
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Leverage Our Global Network</h2>
          <p className="text-xl mb-8 text-primary-foreground/90">
            Get the benefits of international manufacturing with the simplicity of working with a Canadian partner
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground">
              Request a Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
